import cx from 'classnames'
import { useCalendar, useCalendarCell, useCalendarGrid } from '@react-aria/calendar'
import { useCalendarState } from '@react-stately/calendar'
import {
  DateValue,
  getWeeksInMonth,
  createCalendar,
  parseDate,
  isSameDay,
  isToday,
  getLocalTimeZone,
} from '@internationalized/date'
import { CalendarProps, AriaMappedProps, AriaUnmappedProps, CalendarCellProps, CalendarGridProps } from './Types'
import { useLocale } from '@react-aria/i18n'
import { useObjectRef } from '@react-aria/utils'
import Styles from './Calendar.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Button } from 'blaze-ui/Button'
import { ChevronLeft, ChevronRight } from 'blaze-ui/icons/24'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import dayjs, { Dayjs } from 'dayjs'

const mapProps = ({
  value,
  onChange,
  minValue,
  maxValue,
  isDateUnavailable,
  focusedValue,
  defaultFocusedValue,
  onFocusChange,
  defaultValue,
  ...rest
}: AriaUnmappedProps) => {
  const mappedProps: AriaMappedProps = { ...rest }

  if (typeof value === 'string') {
    mappedProps.value = value.length > 0 ? parseDate(value) : null
  } else if (onChange) {
    // If the value is undefined but we're using the component in a controlled manner, keep the value as controlled 'empty'
    mappedProps.value = null
  }

  if (defaultValue) {
    mappedProps.defaultValue = parseDate(defaultValue)
  }

  if (onChange) {
    mappedProps.onChange = (date: DateValue) => {
      if (mappedProps.value && isSameDay(mappedProps.value, date)) {
        onChange(undefined)
      } else {
        onChange(date.toString())
      }
    }
  }

  if (minValue) {
    mappedProps.minValue = parseDate(minValue)
  }

  if (maxValue) {
    mappedProps.maxValue = parseDate(maxValue)
  }

  if (isDateUnavailable) {
    mappedProps.isDateUnavailable = (date: DateValue) => isDateUnavailable(date.toString())
  }

  if (focusedValue) {
    mappedProps.focusedValue = parseDate(focusedValue)
  }

  if (defaultFocusedValue) {
    mappedProps.defaultFocusedValue = parseDate(defaultFocusedValue)
  }

  if (onFocusChange) {
    mappedProps.onFocusChange = (date: DateValue) => onFocusChange(date.toString())
  }

  return mappedProps
}

const CalendarCell = ({ state, date, datesWithEvents = [] }: CalendarCellProps & { datesWithEvents?: Dayjs[] }) => {
  let ref = useObjectRef<HTMLDivElement>()
  let {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    isUnavailable,
    isFocused,
    formattedDate,
  } = useCalendarCell({ date }, state, ref)

  const hasEvent = datesWithEvents.some((eventDate) => eventDate.isSame(dayjs(date.toString()), 'day'))

  return (
    <td {...cellProps}>
      <div
        {...buttonProps}
        ref={ref}
        hidden={isOutsideVisibleRange}
        className={cx(Styles.cell, {
          [Styles.selected]: isSelected,
          [Styles.disabled]: isDisabled,
          [Styles.unavailable]: isUnavailable,
          [Styles.focused]: isFocused,
          [Styles.today]: isToday(date, getLocalTimeZone()),
        })}
        data-testid="select-date-button"
      >
        {formattedDate}
        {hasEvent ? <Dot fill={isSelected ? 'var(--light-60)' : 'var(--dark-25)'} /> : null}
      </div>
    </td>
  )
}

const CalendarGrid = ({
  state,
  tableClassName,
  datesWithEvents = [],
  ...props
}: CalendarGridProps & { datesWithEvents?: Dayjs[] }) => {
  let { locale } = useLocale()
  let { gridProps, headerProps, weekDays } = useCalendarGrid(props, state)

  // Get the number of weeks in the month so we can render the proper number of rows.
  let weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale)

  return (
    <table {...gridProps} className={tableClassName}>
      <thead {...headerProps}>
        <tr>
          {weekDays.map((day, index) => (
            <th key={index}>
              <div className={Styles.dayHeader}>{day}</div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr key={weekIndex}>
            {state
              .getDatesInWeek(weekIndex)
              .map((date, i) =>
                date ? (
                  <CalendarCell key={i} state={state} date={date} datesWithEvents={datesWithEvents} />
                ) : (
                  <td key={i} />
                ),
              )}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const Calendar = ({
  responsive = false,
  headerSize = 'md',
  isAPMobileWeb = false,
  datesWithEvents = [],
  ...props
}: CalendarProps) => {
  const { locale } = useLocale()
  const { showClearDate, value, onChange } = props
  const mappedProps = mapProps(props)
  const state = useCalendarState({ ...mappedProps, locale, createCalendar })
  const { calendarProps, prevButtonProps, nextButtonProps, title } = useCalendar(mappedProps, state)
  const { isMobile } = useResponsiveDesign()
  const calendarClass = cx(Styles.calendar, { [Styles.responsive]: responsive })

  const iconSize = isMobile && responsive ? 'xl' : 'md'

  return (
    <div {...calendarProps} className={cx(calendarClass, { [Styles.apMobileWeb]: isAPMobileWeb })}>
      <div className={cx(Styles.header, Styles[headerSize])}>
        <span className={Styles.title}>{title}</span>
        <div className={Styles.chevronButtons}>
          <IconButton icon={ChevronLeft} variant="editor" size={iconSize} {...prevButtonProps} />
          <IconButton icon={ChevronRight} variant="editor" size={iconSize} {...nextButtonProps} />
        </div>
      </div>
      <CalendarGrid state={state} tableClassName={Styles.calendarTable} datesWithEvents={datesWithEvents} />
      {showClearDate && value && (
        <div className={Styles.footer}>
          <Button fullWidth size="sm" variant="editor" onPress={() => onChange?.(undefined)}>
            Clear date
          </Button>
        </div>
      )}
    </div>
  )
}

const Dot = ({ fill }: { fill: string }) => {
  return (
    <svg className={Styles.dot} xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4">
      <circle cx="2" cy="2" r="2" fill={fill} />
    </svg>
  )
}

export { Calendar }
